import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismNumberInput } from '@mylighthouse/prism-ember';
import type {
  CustomEmberTableColumn,
  CustomEmberTableRow,
} from '@mylighthouse/prism-ember/types/components/prism/table';
import { task } from 'ember-concurrency';
import { eq } from 'ember-truth-helpers';
import type { RecommendationStrategy } from 'lumen/models';
import type {
  ComputedValueCondition,
  OperandCondition,
  UserEditableCondition,
} from 'lumen/models/recommendation-strategy';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import { timeout } from 'lumen/utils/waiters';

type StrategyTableColumn = CustomEmberTableColumn & {
  id: number;
  isEditing: boolean;
  isSaveStrategyTaskRunning: boolean;
  onDayOfWeekValueChange: (
    dow: number,
    setting: string,
    isConditional: boolean,
    value: number | null,
  ) => void;
  strategy: RecommendationStrategy;
};

type StrategyTableRow = CustomEmberTableRow & {
  label: string;
  setting: string;
};

interface SettingsRecommendationsPriceRangeTdDowSignature {
  Args: {
    column: StrategyTableColumn;
    row: StrategyTableRow;
    value: {
      formattedValue: string;
      isConditional: boolean;
      rawValue: number;
    };
  };
}

export default class SettingsRecommendationsPriceRangeTdDow extends Component<SettingsRecommendationsPriceRangeTdDowSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];

  /*
    The input changes are debounced only when typing
    On focus out, the value is set immediately to check for errors
  */
  debouncedOnDayOfWeekValueChangeTask = task(
    { restartable: true },
    async (dowValue: number | null): Promise<void> => {
      await timeout('extra-long');
      this.setDayOfWeekValue(dowValue);
    },
  );

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get conditions() {
    return this.args.column.strategy.conditionalStrategies?.[0]
      ?.conditions as OperandCondition & {
      first?: ComputedValueCondition;
      second?: UserEditableCondition;
    };
  }

  // Input field is disabled if no conditional value is set or save strategy task is running
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get numberInputDisabled() {
    if (this.args.column.isSaveStrategyTaskRunning) {
      return true;
    }

    return (
      this.args.value.isConditional && this.conditions?.second?.value === null
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get numberInputHasErrors() {
    // Check if input field belongs to column containing day of week with errors
    return this.args.column.strategy.priceRangeStrategyDayOfWeekWithErrors.some(
      (day) =>
        day.dow === this.args.column.id &&
        day.isConditional === this.args.value.isConditional,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get numberInputPlaceholder() {
    if (this.args.row.setting === 'minimum') {
      return this.intl.t(
        'pricing_assistant.recommendations.price_range.no_min',
      );
    }

    if (this.args.row.setting === 'maximum') {
      return this.intl.t(
        'pricing_assistant.recommendations.price_range.no_max',
      );
    }

    return '';
  }

  setDayOfWeekValue(dowValue: number | null): void {
    const { column, row, value } = this.args;

    this.args.column.onDayOfWeekValueChange(
      column.id,
      row.setting,
      value.isConditional,
      dowValue,
    );
  }

  @action onDayOfWeekFocusOut(event: Event): void {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.debouncedOnDayOfWeekValueChangeTask.cancelAll();
    const target = event.target as HTMLInputElement;
    const value = target?.value ? parseInt(target.value, 10) : null;
    this.setDayOfWeekValue(value);
  }

  @action onDayOfWeekValueChange(dowValue: number | null): void {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.debouncedOnDayOfWeekValueChangeTask.perform(dowValue);
  }

  <template>
    {{#unless (eq @row.setting "condition")}}
      {{#if @column.isEditing}}
        <PrismNumberInput
          @disabled={{this.numberInputDisabled}}
          @hasErrors={{this.numberInputHasErrors}}
          @inlineLabel={{this.hotelAndMarketService.hotel.currencySymbol}}
          @isSmall={{true}}
          @isTouched={{true}}
          @min={{0}}
          @onFocusOut={{this.onDayOfWeekFocusOut}}
          @onValueChange={{this.onDayOfWeekValueChange}}
          @placeholder={{this.numberInputPlaceholder}}
          @removePadding={{true}}
          @value={{@value.rawValue}}
        />
      {{else}}
        {{@value.formattedValue}}
      {{/if}}
    {{/unless}}
  </template>
}
